import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Play, Square, Trash2, Timer, ArrowUp, ArrowDown, ArrowUpDown, Clock, Users } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

function fmt(sec) {
  sec = Math.floor(sec);
  const h = String(Math.floor(sec / 3600)).padStart(2, "0");
  const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
  const s = String(sec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="card-tech p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-mini">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="mt-3 font-heading text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function SortButton({ label, active, dir, onClick, align, testid }) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${active ? "text-foreground" : ""} ${align === "right" ? "justify-end w-full" : ""}`}
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
}

// ----------- Team Leader View ---------------
const LEADER_COLS = [
  { key: "name", label: "Nama", align: "left", getter: (m) => (m.name || "").toLowerCase() },
  { key: "total_hours", label: "Total Jam", align: "right", getter: (m) => Number(m.total_hours || 0) },
  { key: "week_hours", label: "Minggu Ini", align: "right", getter: (m) => Number(m.week_hours || 0) },
  { key: "today_hours", label: "Hari Ini", align: "right", getter: (m) => Number(m.today_hours || 0) },
  { key: "avg_daily_hours", label: "Rata-rata/Hari", align: "right", getter: (m) => Number(m.avg_daily_hours || 0) },
  { key: "entries_count", label: "Entries", align: "right", getter: (m) => Number(m.entries_count || 0) },
  { key: "active_days", label: "Hari Aktif", align: "right", getter: (m) => Number(m.active_days || 0) },
  { key: "last_activity", label: "Aktivitas Terakhir", align: "left", getter: (m) => m.last_activity || "" },
];

function LeaderTimesheet() {
  const [stats, setStats] = useState(null);
  const [sortKey, setSortKey] = useState("total_hours");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/timesheet/team-stats");
        setStats(data);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    })();
  }, []);

  const sorted = useMemo(() => {
    if (!stats) return [];
    const col = LEADER_COLS.find((c) => c.key === sortKey);
    if (!col) return stats.members;
    return [...stats.members].sort((a, b) => {
      const av = col.getter(a);
      const bv = col.getter(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [stats, sortKey, sortDir]);

  const [pageItems, ctrl] = usePagination(sorted, 10);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "name" ? "asc" : "desc"); }
  };

  if (!stats) return <div className="text-muted-foreground">Loading…</div>;
  const totals = stats.totals;

  return (
    <div className="space-y-8" data-testid="timesheet-page">
      <div>
        <div className="label-mini">Timesheet · Team View</div>
        <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Statistik Waktu Tim</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Rekap timesheet seluruh member presales. Klik header kolom untuk mengurutkan.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Member" value={totals.members_count} icon={Users} testid="ts-stat-members" />
        <StatCard label="Total Jam" value={`${totals.total_hours}h`} icon={Clock} testid="ts-stat-total" />
        <StatCard label="Minggu Ini" value={`${totals.week_hours}h`} icon={Clock} testid="ts-stat-week" />
        <StatCard label="Hari Ini" value={`${totals.today_hours}h`} icon={Clock} testid="ts-stat-today" />
      </div>

      <div className="card-tech p-0 overflow-hidden">
        <div className="p-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-mini">All Members</div>
            <h3 className="font-heading text-xl font-bold mt-1">Rekap Waktu per Member</h3>
          </div>
          <div className="text-xs text-muted-foreground">
            Urut ({sortDir === "asc" ? "asc" : "desc"} · {LEADER_COLS.find((c) => c.key === sortKey)?.label})
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-timesheet-table">
            <thead className="bg-accent text-xs uppercase tracking-widest">
              <tr>
                {LEADER_COLS.map((c) => (
                  <th key={c.key} className={`px-6 py-3 ${c.align === "right" ? "text-right" : "text-left"}`}>
                    <SortButton
                      label={c.label}
                      active={sortKey === c.key}
                      dir={sortDir}
                      onClick={() => toggleSort(c.key)}
                      align={c.align}
                      testid={`ts-sort-${c.key}`}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={LEADER_COLS.length} className="px-6 py-10 text-center text-muted-foreground">Belum ada data timesheet.</td></tr>
              )}
              {pageItems.map((m) => (
                <tr key={m.user_id} className="border-t border-border hover:bg-accent/50 transition-colors" data-testid={`ts-member-row-${m.user_id}`}>
                  <td className="px-6 py-3">
                    <div className="font-medium">{m.name}</div>
                    <div className="text-xs text-muted-foreground mono">{m.email}</div>
                  </td>
                  <td className="px-6 py-3 text-right mono">{m.total_hours}h</td>
                  <td className="px-6 py-3 text-right mono">{m.week_hours}h</td>
                  <td className="px-6 py-3 text-right mono">{m.today_hours}h</td>
                  <td className="px-6 py-3 text-right mono">{m.avg_daily_hours}h</td>
                  <td className="px-6 py-3 text-right mono">{m.entries_count}</td>
                  <td className="px-6 py-3 text-right mono">{m.active_days}</td>
                  <td className="px-6 py-3 text-xs text-muted-foreground mono">
                    {m.last_activity ? new Date(m.last_activity).toLocaleString("id-ID") : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination ctrl={ctrl} testidPrefix="ts-leader-pg" />
      </div>
    </div>
  );
}

// ----------- Member View --------------------
function MemberTimesheet() {
  const { isReadOnly } = useAuth();
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [task, setTask] = useState("");
  const [project, setProject] = useState("");
  const [running, setRunning] = useState(null); // {start, task, project}
  const [, setTick] = useState(0);
  const [manual, setManual] = useState({ task_name: "", hours: 1, note: "" });
  const timerRef = useRef(null);
  const [pageItems, ctrl] = usePagination(items, 10);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/timesheet"),
      api.get("/task-templates"),
      api.get("/projects"),
    ]);
    setItems(a.data); setTemplates(b.data); setProjects(c.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (running) {
      timerRef.current = setInterval(() => setTick((t) => t + 1), 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [running]);

  const start = () => {
    if (!task.trim()) { toast.error("Isi task terlebih dahulu"); return; }
    setRunning({ start: Date.now(), task, project });
    setTick(0);
  };

  const stop = async () => {
    if (!running) return;
    const end = Date.now();
    const dur = Math.floor((end - running.start) / 1000);
    try {
      await api.post("/timesheet", {
        task_name: running.task,
        project_id: running.project || null,
        start_time: new Date(running.start).toISOString(),
        end_time: new Date(end).toISOString(),
        duration_seconds: dur,
        note: "",
      });
      toast.success("Timesheet tersimpan");
      setRunning(null);
      setTask("");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    const now = new Date();
    const dur = Math.round(Number(manual.hours) * 3600);
    try {
      await api.post("/timesheet", {
        task_name: manual.task_name,
        start_time: new Date(now.getTime() - dur * 1000).toISOString(),
        end_time: now.toISOString(),
        duration_seconds: dur,
        note: manual.note,
      });
      toast.success("Timesheet tersimpan");
      setManual({ task_name: "", hours: 1, note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (id) => { await api.delete(`/timesheet/${id}`); load(); };

  const elapsed = running ? Math.floor((Date.now() - running.start) / 1000) : 0;

  return (
    <div className="space-y-8" data-testid="timesheet-page">
      <div>
        <div className="label-mini">Timesheet</div>
        <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Timer Tracker</h1>
        <p className="text-sm text-muted-foreground mt-2">Mulai timer untuk task saat ini atau input jam manual.</p>
      </div>

      {/* Timer */}
      {!isReadOnly && (
      <div className="card-tech p-6 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="timer-panel">
        <div className="lg:col-span-2 space-y-3">
          <Label className="label-mini">Task Sekarang</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              placeholder="Nama task, mis. Prep proposal ABC"
              value={task}
              onChange={(e) => setTask(e.target.value)}
              disabled={!!running}
              data-testid="timer-task-input"
            />
            <Select value={project} onValueChange={setProject} disabled={!!running}>
              <SelectTrigger data-testid="timer-project-select"><SelectValue placeholder="(Opsional) Pilih project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {templates.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="label-mini self-center">Template:</span>
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTask(t.name)}
                  disabled={!!running}
                  data-testid={`template-chip-${t.id}`}
                  className="text-xs px-3 py-1 rounded-full border border-border hover:bg-accent"
                >{t.name}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="mono text-4xl font-black tracking-tight" data-testid="timer-display">{fmt(elapsed)}</div>
          {!running ? (
            <Button onClick={start} className="w-full rounded-sm" data-testid="start-timer-button">
              <Play className="h-4 w-4 mr-2" /> Start
            </Button>
          ) : (
            <Button onClick={stop} variant="destructive" className="w-full rounded-sm" data-testid="stop-timer-button">
              <Square className="h-4 w-4 mr-2" /> Stop & Save
            </Button>
          )}
        </div>
      </div>
      )}

      {/* Manual entry */}
      {!isReadOnly && (
      <form onSubmit={addManual} className="card-tech p-6 grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="manual-form">
        <div className="md:col-span-2">
          <Label className="label-mini">Task</Label>
          <Input value={manual.task_name} onChange={(e) => setManual({ ...manual, task_name: e.target.value })} required data-testid="manual-task-input" />
        </div>
        <div>
          <Label className="label-mini">Jam</Label>
          <Input type="number" step="0.25" min="0.25" value={manual.hours} onChange={(e) => setManual({ ...manual, hours: e.target.value })} data-testid="manual-hours-input" />
        </div>
        <div className="md:col-span-2 flex items-end gap-3">
          <Input placeholder="Catatan" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} data-testid="manual-note-input" />
          <Button type="submit" className="rounded-sm shrink-0" data-testid="add-manual-button"><Timer className="h-4 w-4 mr-2" />Add</Button>
        </div>
      </form>
      )}

      {/* List */}
      <div className="card-tech p-0 overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="label-mini">History</div>
          <h3 className="font-heading text-xl font-bold mt-1">Entries</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-6 py-3">Task</th>
                <th className="text-left px-6 py-3">Project</th>
                <th className="text-left px-6 py-3">Start</th>
                <th className="text-right px-6 py-3">Durasi</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (<tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Belum ada entry.</td></tr>)}
              {pageItems.map((it) => (
                <tr key={it.id} className="border-t border-border hover:bg-accent/50" data-testid={`timesheet-row-${it.id}`}>
                  <td className="px-6 py-3 font-medium">{it.task_name}</td>
                  <td className="px-6 py-3 text-muted-foreground">{projects.find((p) => p.id === it.project_id)?.name || "-"}</td>
                  <td className="px-6 py-3 mono text-xs">{new Date(it.start_time).toLocaleString("id-ID")}</td>
                  <td className="px-6 py-3 text-right mono">{fmt(it.duration_seconds)}</td>
                  <td className="px-6 py-3 text-right">
                    {!isReadOnly && (
                      <Button size="sm" variant="ghost" onClick={() => remove(it.id)} data-testid={`delete-timesheet-${it.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination ctrl={ctrl} testidPrefix="ts-pg" />
      </div>
    </div>
  );
}

export default function Timesheet() {
  const { user } = useAuth();
  if (user?.role === "team_leader") return <LeaderTimesheet />;
  return <MemberTimesheet />;
}
